/**
 * Module 00 — Global Shell · Mode switch (master Simple/Advanced toggle).
 */

'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Icon } from './icons';
import type { Mode } from '@/lib/shell/persistence';

export function ModeSwitch({
  mode,
  setMode,
  compact = false,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
  /** Icon-only rendering for the collapsed (64px) sidebar rail. */
  compact?: boolean;
}) {
  const on = mode === 'simple';
  const toggle = () => setMode(on ? 'advanced' : 'simple');

  if (compact) {
    return (
      <button
        type="button"
        data-testid="mode-switch"
        role="switch"
        aria-checked={on}
        aria-label={on ? 'Switch to advanced mode' : 'Switch to simple mode'}
        title={on ? 'Switch to advanced mode' : 'Switch to simple mode'}
        onClick={toggle}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-cs-surface-hover',
          on ? 'text-cs-primary-400' : 'text-cs-text-secondary hover:text-cs-text-primary',
        )}
      >
        <Icon name="zap" size={16} />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="mode-switch"
      role="switch"
      aria-checked={on}
      aria-label={on ? 'Switch to advanced mode' : 'Switch to simple mode'}
      title={on ? 'Switch to advanced mode' : 'Switch to simple mode'}
      onClick={toggle}
      className="flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium text-cs-text-secondary transition-colors hover:text-cs-text-primary"
    >
      <span className="flex items-center gap-1.5">
        <Icon name="zap" size={14} />
        Simple mode
      </span>
      <span
        className={cn(
          'relative h-4 w-7 rounded-full transition-colors',
          on ? 'bg-cs-primary-500' : 'bg-cs-surface-hover',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left]',
            on ? 'left-[14px]' : 'left-0.5',
          )}
        />
      </span>
    </button>
  );
}
